import * as Yup from "yup";
import { ISubAccountFormValues } from "../types";

const phoneRegExp = /\(\d{2,}\) \d{4,}-\d{4}/g;

export type SubAccountFormMode = "create" | "edit";

interface ValidateSubAccountFormOptions {
  lockIdentityFields?: boolean;
}

export async function validateSubAccountForm(
  values: ISubAccountFormValues,
  mode: SubAccountFormMode,
  options: ValidateSubAccountFormOptions = {}
): Promise<Partial<Record<keyof ISubAccountFormValues, string>>> {
  const { lockIdentityFields = false } = options;

  const passwordSchema =
    mode === "create"
      ? Yup.string()
          .required("Senha obrigatória")
          .min(6, "A senha deve ter no mínimo 6 caracteres")
      : Yup.string().test(
          "optional-min",
          "A senha deve ter no mínimo 6 caracteres",
          (value) => !value || value.length >= 6
        );

  const passwordConfirmationSchema =
    mode === "create"
      ? Yup.string()
          .required("Confirmação de senha obrigatória")
          .oneOf([Yup.ref("password")], "As senhas não conferem")
      : Yup.string().when("password", {
          is: (password: string) => Boolean(password && password.length > 0),
          then: Yup.string()
            .required("Confirmação de senha obrigatória")
            .oneOf([Yup.ref("password")], "As senhas não conferem"),
          otherwise: Yup.string(),
        });

  const schema = Yup.object().shape({
    first_name: lockIdentityFields
      ? Yup.string()
      : Yup.string().required("Nome obrigatório"),
    email: lockIdentityFields
      ? Yup.string()
      : Yup.string().email("E-mail inválido").required("E-mail obrigatório"),
    phone: lockIdentityFields
      ? Yup.string()
      : Yup.string()
          .required("Telefone obrigatório")
          .matches(phoneRegExp, "Número inválido"),
    password: lockIdentityFields ? Yup.string() : passwordSchema,
    password_confirmation: lockIdentityFields
      ? Yup.string()
      : passwordConfirmationSchema,
    property_ids: Yup.array()
      .of(Yup.number())
      .when("assign_all_properties", {
        is: false,
        then: Yup.array()
          .of(Yup.number())
          .min(1, "Selecione pelo menos um imóvel"),
      }),
    assign_all_properties: Yup.boolean(),
    active: Yup.boolean(),
  });

  try {
    await schema.validate(values, { abortEarly: false });
  } catch (error) {
    if (!(error instanceof Yup.ValidationError)) {
      return { first_name: "Não foi possível validar o formulário." };
    }

    return error.inner.reduce<Partial<Record<keyof ISubAccountFormValues, string>>>(
      (acc, item) => {
        if (item.path && !acc[item.path as keyof ISubAccountFormValues]) {
          acc[item.path as keyof ISubAccountFormValues] = item.message;
        }
        return acc;
      },
      {}
    );
  }

  return {};
}
